import { createSlice } from "@reduxjs/toolkit";
import {
  getContractsNewThunk,
  getContractNewByIdThunk,
  createContractNewThunk,
  updateContractNewThunk,
  submitContractThunk,
  approveContractThunk,
  rejectContractThunk,
  activateContractThunk,
  terminateContractThunk,
  deleteContractThunk,
  deleteContractPartyThunk,
  updateContractPartyThunk,
  amendContractThunk,
  addContractUnitsThunk,
  getContractCostsThunk,
  addContractCostsThunk,
  updateContractCostThunk,
  deleteContractCostThunk,
  setRevenueSchemeThunk,
  addContractPartiesThunk,
  getContractVersionsThunk,
  getContractServicesThunk,
  setContractServicesThunk,
  getServiceUnitsThunk,
  addServiceUnitThunk,
  removeServiceUnitThunk,
  getCostPeriodsThunk,
  createCostPeriodThunk,
  updateCostPeriodThunk,
  deleteCostPeriodThunk,
  getPeriodCostsThunk,
  addPeriodCostThunk,
  updatePeriodCostThunk,
  deletePeriodCostThunk,
  getCostUnitsThunk,
  addCostUnitThunk,
  updateCostUnitThunk,
  deleteCostUnitThunk,
} from "state/action/contract_new";

const asyncState = (data = null) => ({
  data,
  isPending: false,
  isFulfilled: false,
  isRejected: false,
  err: null,
});

const initialState = {
  getContracts: asyncState(),
  getContractById: asyncState(),
  createContract: asyncState(),
  updateContract: asyncState(),
  submitContract: asyncState(),
  approveContract: asyncState(),
  rejectContract: asyncState(),
  activateContract: asyncState(),
  terminateContract: asyncState(),
  deleteContract: asyncState(),
  amendContract: asyncState(),
  addUnits: asyncState(),
  getServices: asyncState(),
  setServices: asyncState(),
  getContractCosts: asyncState(),
  addCosts: asyncState(),
  updateCost: asyncState(),
  deleteCost: asyncState(),
  setRevenueScheme: asyncState(),
  addParties: asyncState(),
  getVersions: asyncState(),
  // ServiceUnit
  getServiceUnits: asyncState(),
  addServiceUnit: asyncState(),
  removeServiceUnit: asyncState(),
  // ContractCostPeriod
  getCostPeriods: asyncState(),
  createCostPeriod: asyncState(),
  updateCostPeriod: asyncState(),
  deleteCostPeriod: asyncState(),
  // ContractCost under period
  getPeriodCosts: asyncState(),
  addPeriodCost: asyncState(),
  updatePeriodCost: asyncState(),
  deletePeriodCost: asyncState(),
  // ContractCostUnit
  getCostUnits: asyncState(),
  addCostUnit: asyncState(),
  updateCostUnit: asyncState(),
  deleteCostUnit: asyncState(),
  selectedContract: null,

  // Draft state for the multi-step CreateContractPage form. Persisted
  // automatically (see index.js persistConfig has no whitelist), so a
  // refresh mid-form keeps the user's progress.
  activeStep: 0,
  contractId: null,
  info: {
    contractNo: "",
    title: "",
    startDate: "",
    endDate: "",
    signDate: "",
    effective_date: "",
    total_period: "",
    isSameEffectiveDate: false,
    efective_terms: [
      {
        term: "",
        efiden: { file: "", url: "" },
        due_date: "",
        status: "",
        isFinish: false,
      },
    ],
  },
  infoErrors: {},
  expandedCosts: {},
  selectedServices: [],
  filterProperty: "",
  filterBuilding: "",
  filterFloor: "",
  selectedUnits: [],
  contractServiceId: null,
  periods: [{ label: "", periodStart: "", periodEnd: "", costs: [] }],
  scheme: {
    type: "REGULAR",
    shareBasis: "GROSS_SALES",
    revenueShareMethod: "",
    flatRate: { isActive: false, ratePercent: "", salesAmount: "" },
    mgp: {
      isActive: false,
      mgpAmount: "",
      mgpRatePercent: "",
      mgpBasis: "per_month",
      mgMode: "GREATER_OF",
    },
    yearlyRates: [],
    conditionalRate: { tierMode: "MARGINAL", tiers: [] },
    tieredScheme: { tiers: [] },
    salesBracket: {
      lowRate: 7,
      threshold: 100000000,
      highRate: 10,
      maxRevShare: 10000000,
    },
    settlementSchedule: {
      frequency: "",
      daily: { hour: "08" },
      weekly: { day: "1" },
      monthly: { date: "1" },
      quarterly: { monthOffset: 1, date: "1" },
      bimonthly: { monthOffset: 1, date: "1" },
      fourmonthly: { monthOffset: 1, date: "1" },
      semester: { monthOffset: 1, date: "1" },
      yearly: { date: "" },
      billingConfig: {
        billingOffsetDays: 0,
        dueOffsetDays: 14,
        revenueRecognitionOffsetDays: 30,
        salesRecapitulationDays: 0,
      },
    },
  },
  parties: [
    {
      mitraId: "",
      role: "PIHAK_1",
      signatory: "",
      wakil: "",
      obligations: [""],
      rights: [""],
    },
  ],
};

function makeCases(builder, thunk, stateKey) {
  builder
    .addCase(thunk.pending, (state) => {
      state[stateKey] = {
        data: null,
        isPending: true,
        isFulfilled: false,
        isRejected: false,
        err: null,
      };
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey] = {
        data: action.payload,
        isPending: false,
        isFulfilled: true,
        isRejected: false,
        err: null,
      };
    })
    .addCase(thunk.rejected, (state, action) => {
      state[stateKey] = {
        data: null,
        isPending: false,
        isFulfilled: false,
        isRejected: true,
        err: action.error.message,
      };
    });
}

export const contractNewSlice = createSlice({
  name: "contractNew",
  initialState,
  reducers: {
    setSelectedContract: (state, action) => {
      state.selectedContract = action.payload;
    },
    setField: (state, action) => {
      state[action.payload.key] = action.payload.value;
    },
    resetForm: (state) => {
      const { activeStep, contractId, info, infoErrors, expandedCosts,
        selectedServices, filterProperty, filterBuilding, filterFloor,
        selectedUnits, contractServiceId, periods, scheme, parties } = initialState;
      Object.assign(state, { activeStep, contractId, info, infoErrors,
        expandedCosts, selectedServices, filterProperty, filterBuilding,
        filterFloor, selectedUnits, contractServiceId, periods, scheme, parties });
    },
    clearGetContracts: (state) => {
      state.getContracts = asyncState();
    },
    clearGetContractById: (state) => {
      state.getContractById = asyncState();
    },
    clearCreateContract: (state) => {
      state.createContract = asyncState();
    },
    clearUpdateContract: (state) => {
      state.updateContract = asyncState();
    },
  },
  extraReducers: (builder) => {
    makeCases(builder, getContractsNewThunk, "getContracts");
    makeCases(builder, getContractNewByIdThunk, "getContractById");
    makeCases(builder, createContractNewThunk, "createContract");
    makeCases(builder, updateContractNewThunk, "updateContract");
    makeCases(builder, submitContractThunk, "submitContract");
    makeCases(builder, approveContractThunk, "approveContract");
    makeCases(builder, rejectContractThunk, "rejectContract");
    makeCases(builder, activateContractThunk, "activateContract");
    makeCases(builder, terminateContractThunk, "terminateContract");
    makeCases(builder, deleteContractThunk, "deleteContract");
    makeCases(builder, amendContractThunk, "amendContract");
    makeCases(builder, addContractUnitsThunk, "addUnits");
    makeCases(builder, getContractServicesThunk, "getServices");
    makeCases(builder, setContractServicesThunk, "setServices");
    makeCases(builder, getContractCostsThunk, "getContractCosts");
    makeCases(builder, addContractCostsThunk, "addCosts");
    makeCases(builder, updateContractCostThunk, "updateCost");
    makeCases(builder, deleteContractCostThunk, "deleteCost");
    makeCases(builder, setRevenueSchemeThunk, "setRevenueScheme");
    makeCases(builder, addContractPartiesThunk, "addParties");
    makeCases(builder, getContractVersionsThunk, "getVersions");
    // ServiceUnit
    makeCases(builder, getServiceUnitsThunk, "getServiceUnits");
    makeCases(builder, addServiceUnitThunk, "addServiceUnit");
    makeCases(builder, removeServiceUnitThunk, "removeServiceUnit");
    // ContractCostPeriod
    makeCases(builder, getCostPeriodsThunk, "getCostPeriods");
    makeCases(builder, createCostPeriodThunk, "createCostPeriod");
    makeCases(builder, updateCostPeriodThunk, "updateCostPeriod");
    makeCases(builder, deleteCostPeriodThunk, "deleteCostPeriod");
    // ContractCost under period
    makeCases(builder, getPeriodCostsThunk, "getPeriodCosts");
    makeCases(builder, addPeriodCostThunk, "addPeriodCost");
    makeCases(builder, updatePeriodCostThunk, "updatePeriodCost");
    makeCases(builder, deletePeriodCostThunk, "deletePeriodCost");
    // ContractCostUnit
    makeCases(builder, getCostUnitsThunk, "getCostUnits");
    makeCases(builder, addCostUnitThunk, "addCostUnit");
    makeCases(builder, updateCostUnitThunk, "updateCostUnit");
    makeCases(builder, deleteCostUnitThunk, "deleteCostUnit");
  },
});

export const contractNewAction = {
  ...contractNewSlice.actions,
  getContractsNewThunk,
  getContractNewByIdThunk,
  createContractNewThunk,
  updateContractNewThunk,
  submitContractThunk,
  approveContractThunk,
  rejectContractThunk,
  activateContractThunk,
  terminateContractThunk,
  deleteContractThunk,
  amendContractThunk,
  addContractUnitsThunk,
  getContractServicesThunk,
  setContractServicesThunk,
  getContractCostsThunk,
  addContractCostsThunk,
  updateContractCostThunk,
  deleteContractCostThunk,
  setRevenueSchemeThunk,
  addContractPartiesThunk,
  getContractVersionsThunk,
  updateContractPartyThunk,
  deleteContractPartyThunk,
  // ServiceUnit
  getServiceUnitsThunk,
  addServiceUnitThunk,
  removeServiceUnitThunk,
  // ContractCostPeriod
  getCostPeriodsThunk,
  createCostPeriodThunk,
  updateCostPeriodThunk,
  deleteCostPeriodThunk,
  // ContractCost under period
  getPeriodCostsThunk,
  addPeriodCostThunk,
  updatePeriodCostThunk,
  deletePeriodCostThunk,
  // ContractCostUnit
  getCostUnitsThunk,
  addCostUnitThunk,
  updateCostUnitThunk,
  deleteCostUnitThunk,
};

export function selectContractNew(state) {
  return state?.contractNew;
}

export default contractNewSlice.reducer;
